// FIXME: Update this file to be type safe and remove this and next line
// @ts-strict-ignore
import { Router } from "@angular/router";
import { firstValueFrom, Observable, ReplaySubject } from "rxjs";

import { AccountService } from "@bitwarden/common/auth/abstractions/account.service";
import {
  Environment,
  Region,
  RegionConfig,
  Urls,
} from "@bitwarden/common/platform/abstractions/environment.service";
// IDA CUSTOMIZATION: Added Utils for safe domain matching (avoids new URL() crash on empty URLs)
import { Utils } from "@bitwarden/common/platform/misc/utils";
import {
  CloudEnvironment,
  DefaultEnvironmentService,
  SelfHostedEnvironment,
} from "@bitwarden/common/platform/services/default-environment.service";
import { StateProvider } from "@bitwarden/common/platform/state";
import { UserId } from "@bitwarden/user-core";

// IDA CUSTOMIZATION: Import config.json to load IDA-specific API URLs at runtime
// This file is injected with environment-specific URLs during CD deployment via variable substitution
import config from "../../config.json";

export type WebRegionConfig = RegionConfig & {
  key: Region | string; // strings are used for custom environments
  domain: string;
  urls: Urls;
};

/**
 * Web specific environment service. Ensures that the urls are set from the window location.
 * IDA CUSTOMIZATION: Modified to load URLs from config.json instead of constructor envUrls
 * to support CD pipeline variable substitution for different environments.
 */
export class WebEnvironmentService extends DefaultEnvironmentService {
  private _environmentSubject: ReplaySubject<Environment>;

  constructor(
    private win: Window,
    stateProvider: StateProvider,
    accountService: AccountService,
    additionalRegionConfigs: WebRegionConfig[] = [],
    private router: Router,
    private envUrls: Urls,
  ) {
    super(stateProvider, accountService, additionalRegionConfigs);

    // IDA CUSTOMIZATION START
    // Load URLs from config.json with fallback to constructor envUrls
    // config.json is populated at deploy time by the CD pipeline variable substitution task
    // Bitwarden v2025 removed config.json and passes URLs via constructor — kept for IDA compatibility
    const urls = (config.config.urls as Urls) ?? this.envUrls;
    urls.base ??= this.win.location.origin;

    // IDA CUSTOMIZATION: Use Utils.getDomain for safe domain matching
    // Bitwarden v2025 uses new URL(r.urls.webVault).hostname which crashes if webVault URL
    // is empty or undefined (happens when config.json URLs are not yet substituted)
    const domain = Utils.getDomain(this.win.location.href);
    const availableRegions = this.availableRegions();
    const region = availableRegions.find((r) =>
      // Added null check on r.urls?.webVault to prevent crash on empty URLs
      r.urls?.webVault ? Utils.getDomain(r.urls.webVault) === domain : false,
    );
    // IDA CUSTOMIZATION END

    /* BITWARDEN v2025 ORIGINAL CODE — DO NOT DELETE
     * Removed because new URL() crashes when webVault URL is empty/undefined
     * which happens when config.json is not present or URLs are not substituted
     *
     * envUrls.base ??= this.win.location.origin;
     *
     * const currentHostname = new URL(this.win.location.href).hostname;
     * const availableRegions = this.availableRegions();
     * const region = availableRegions.find((r) => {
     *   const webVaultHostname = new URL(r.urls.webVault).hostname;
     *   return webVaultHostname === currentHostname;
     * });
     */

    let environment: Environment;
    if (region) {
      environment = new WebCloudEnvironment(region, urls);
    } else {
      environment = new SelfHostedEnvironment(urls);
    }

    const subject = new ReplaySubject<Environment>(1);
    subject.next(environment);
    this._environmentSubject = subject;
    this.environment$ = subject.asObservable();
    this.globalEnvironment$ = subject.asObservable();
  }

  async setEnvironment(region: Region | string, urls?: Urls): Promise<Urls> {
    if (region === Region.SelfHosted) {
      throw new Error("setEnvironment does not work in web for self-hosted.");
    }

    // IDA CUSTOMIZATION START
    // Use Utils.getDomain for safe domain matching — same reason as constructor above
    const domain = Utils.getDomain(this.win.location.href);
    const availableRegions = this.availableRegions();
    const currentRegionConfig = availableRegions.find((r) =>
      r.urls?.webVault ? Utils.getDomain(r.urls.webVault) === domain : false,
    );
    // IDA CUSTOMIZATION END

    /* BITWARDEN v2025 ORIGINAL CODE — DO NOT DELETE
     * Removed because new URL() crashes when webVault URL is empty/undefined
     *
     * const currentHostname = new URL(this.win.location.href).hostname;
     * const availableRegions = this.availableRegions();
     * const currentRegionConfig = availableRegions.find((r) => {
     *   const webVaultHostname = new URL(r.urls.webVault).hostname;
     *   return webVaultHostname === currentHostname;
     * });
     */

    if (currentRegionConfig.key === region) {
      // They have selected the current region, return the current env urls
      // We can't return the region urls because the env base url is modified
      // in the constructor to match the current window.location.origin.
      const currentEnv = await firstValueFrom(this.environment$);
      return currentEnv.getUrls();
    }

    const chosenRegionConfig = this.availableRegions().find((r) => r.key === region);

    if (chosenRegionConfig == null) {
      throw new Error("The selected region is not known as an available region.");
    }

    // Preserve the current in app route + params in the new location
    const routeAndParams = `/#${this.router.url}`;
    this.win.location.href = chosenRegionConfig.urls.webVault + routeAndParams;

    // This return shouldn't matter as we are about to leave the current window
    return chosenRegionConfig.urls;
  }

  getEnvironment$(userId: UserId): Observable<Environment> {
    // Web does not support account switching, and even if it did, you'd be required to be the environment of where the application
    // is running.
    return this._environmentSubject.asObservable();
  }
}

export class WebCloudEnvironment extends CloudEnvironment {
  constructor(config: RegionConfig, urls: Urls) {
    super(config);
    // We override the urls to avoid CORS issues
    this.urls = urls;
  }
}
